import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { lockBodyScroll, unlockBodyScroll } from '../../../utils/body-scroll-lock.util';

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
})
export class ModalComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() header: string = '';
  @Input() headerTemplate?: TemplateRef<any>;
  @Input() width: string = '500px';
  @Input() styleClass: string = 'custom-modal';
  /** Optional class applied to the PrimeNG dialog mask (e.g. top-align large wizards). */
  @Input() maskStyleClass: string = '';
  /**
   * Where to render the dialog in the DOM. Defaults to in-place ('self'),
   * which breaks when a card ancestor has `transform` applied (e.g. entrance
   * animations, carousel slides) — that establishes a containing block for
   * this dialog's `position: fixed` mask, trapping it inside the card
   * instead of covering the viewport. Pass 'body' when the modal can be
   * opened from inside such a card.
   */
  @Input() appendTo?: string;
  @Input() modal: boolean = true;
  /** When true (default), locks page scroll while the dialog is visible. */
  @Input() blockScroll: boolean = true;
  @Input() closable: boolean = true;
  @Input() showHeader: boolean = true;
  @Input() draggable: boolean = false;
  @Input() resizable: boolean = false;
  @Input() contentTemplate?: TemplateRef<any>;
  @Input() footerTemplate?: TemplateRef<any>;
  @Input() showFooter: boolean = true;
  @Input() confirmLabel: string = 'Confirm';
  @Input() cancelLabel: string = 'Cancel';
  @Input() showCancelButton: boolean = true;
  @Input() showConfirmButton: boolean = true;
  @Input() confirmButtonDisabled: boolean = false;
  @Input() preventAutoCloseOnCancel: boolean = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onConfirm = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  private scrollLockedByInstance = false;

  ngOnInit(): void {
    this.syncScrollLock();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['modal'] || changes['blockScroll']) {
      this.syncScrollLock();
    }
  }

  ngOnDestroy(): void {
    this.releaseScrollLock();
  }

  private syncScrollLock(): void {
    if (this.shouldLockScroll()) {
      this.acquireScrollLock();
    } else {
      this.releaseScrollLock();
    }
  }

  private shouldLockScroll(): boolean {
    return this.modal && this.blockScroll && this.visible;
  }

  private acquireScrollLock(): void {
    if (this.scrollLockedByInstance) return;
    lockBodyScroll();
    this.scrollLockedByInstance = true;
  }

  private releaseScrollLock(): void {
    if (!this.scrollLockedByInstance) return;
    unlockBodyScroll();
    this.scrollLockedByInstance = false;
  }

  handleVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
    this.syncScrollLock();

    if (!visible) {
      this.onClose.emit();
    }
  }

  handleConfirm(): void {
    // Don't proceed if button is disabled
    if (this.confirmButtonDisabled) {
      return;
    }
    this.onConfirm.emit();
    this.handleVisibleChange(false);
  }

  handleCancel(): void {
    // Emit the cancel event first
    this.onCancel.emit();
    // Only auto-close if preventAutoCloseOnCancel is false
    // This allows parent components to handle async operations before closing
    if (!this.preventAutoCloseOnCancel) {
      this.handleVisibleChange(false);
    }
  }
}

